<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Hospital Dashboard</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript: void(0);">Health Services</a></li>
                      <li class="breadcrumb-item active">Dashboard</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Facility Selector Card -->
            <div class="row mb-4">
              <div class="col-lg-4">
                <FacilitySelector
                    mode="card"
                    @facility-changed="handleFacilityChange"
                />
              </div>
              <div class="col-lg-8">
                <div class="card h-100">
                  <div class="card-body">
                    <div class="d-flex align-items-center mb-3">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                            <span class="avatar-title bg-success-subtle text-success rounded">
                              <i class="mdi mdi-calendar-today font-size-24"></i>
                            </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h5 class="mb-1">Today's Date</h5>
                        <p class="text-muted mb-0">{{ currentDate }}</p>
                      </div>
                      <div class="text-end">
                        <button @click="refreshDashboard" class="btn btn-soft-primary btn-sm" :disabled="loading">
                          <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i> Refresh
                        </button>
                      </div>
                    </div>
                    <div v-if="!selectedFacility" class="alert alert-warning mb-0">
                      <i class="mdi mdi-alert-circle me-2"></i>
                      Please select a facility to view dashboard statistics
                    </div>
                    <div v-else class="text-muted">
                      <i class="mdi mdi-information-outline me-1"></i>
                      Viewing statistics for <strong>{{ selectedFacility.facilityName }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stats Cards Row -->
            <div v-if="selectedFacility" class="row">
              <!-- Today's Visits -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Today's Visits</p>
                        <h4 class="mb-0">{{ dashboard.todayVisits || 0 }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-primary">
                            <span class="avatar-title">
                              <i class="mdi mdi-account-group font-size-24"></i>
                            </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Active Queue -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Active Queue</p>
                        <h4 class="mb-0">{{ dashboard.activeVisits || 0 }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-warning">
                            <span class="avatar-title bg-warning">
                              <i class="mdi mdi-clock-outline font-size-24"></i>
                            </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Admitted Patients -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Admitted (IPD)</p>
                        <h4 class="mb-0">{{ dashboard.admittedPatients || 0 }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-info">
                            <span class="avatar-title bg-info">
                              <i class="mdi mdi-bed font-size-24"></i>
                            </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Today's Revenue -->
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Today's Revenue</p>
                        <h4 class="mb-0">KES {{ numberFormat(dashboard.todayRevenue || 0) }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-success">
                            <span class="avatar-title bg-success">
                              <i class="mdi mdi-cash font-size-24"></i>
                            </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Queue Status & Alerts Row -->
            <div v-if="selectedFacility" class="row">
              <!-- Queue Status -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-body">
                    <h4 class="card-title mb-4">
                      <i class="mdi mdi-format-list-numbered me-2"></i>Queue Status
                    </h4>

                    <div v-if="message" class="alert alert-danger" role="alert">
                      {{ message }}
                    </div>

                    <div v-if="loading" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>

                    <div v-else class="row">
                      <!-- Triage Queue -->
                      <div class="col-md-3 col-sm-6">
                        <div class="p-3 border rounded text-center mb-3">
                          <div class="avatar-sm mx-auto mb-3">
                              <span class="avatar-title bg-danger-subtle text-danger rounded-circle">
                                <i class="mdi mdi-heart-pulse font-size-20"></i>
                              </span>
                          </div>
                          <h5 class="mb-1">{{ queueCounts.triageQueue || 0 }}</h5>
                          <p class="text-muted mb-0">Triage</p>
                        </div>
                      </div>

                      <!-- Lab Queue -->
                      <div class="col-md-3 col-sm-6">
                        <div class="p-3 border rounded text-center mb-3">
                          <div class="avatar-sm mx-auto mb-3">
                              <span class="avatar-title bg-info-subtle text-info rounded-circle">
                                <i class="mdi mdi-flask font-size-20"></i>
                              </span>
                          </div>
                          <h5 class="mb-1">{{ queueCounts.labPending || 0 }}</h5>
                          <p class="text-muted mb-0">Lab Pending</p>
                        </div>
                      </div>

                      <!-- Pharmacy Queue -->
                      <div class="col-md-3 col-sm-6">
                        <div class="p-3 border rounded text-center mb-3">
                          <div class="avatar-sm mx-auto mb-3">
                              <span class="avatar-title bg-success-subtle text-success rounded-circle">
                                <i class="mdi mdi-pill font-size-20"></i>
                              </span>
                          </div>
                          <h5 class="mb-1">{{ queueCounts.prescriptionsPending || 0 }}</h5>
                          <p class="text-muted mb-0">Pharmacy</p>
                        </div>
                      </div>

                      <!-- Low Stock -->
                      <div class="col-md-3 col-sm-6">
                        <div class="p-3 border rounded text-center mb-3">
                          <div class="avatar-sm mx-auto mb-3">
                              <span class="avatar-title bg-warning-subtle text-warning rounded-circle">
                                <i class="mdi mdi-alert font-size-20"></i>
                              </span>
                          </div>
                          <h5 class="mb-1">{{ queueCounts.lowStockAlerts || 0 }}</h5>
                          <p class="text-muted mb-0">Low Stock</p>
                        </div>
                      </div>
                    </div>

                    <!-- Bed Occupancy -->
                    <div class="mt-4">
                      <h5 class="mb-3">Bed Occupancy</h5>
                      <div class="row align-items-center">
                        <div class="col-md-8">
                          <div class="progress" style="height: 20px;">
                            <div
                                class="progress-bar"
                                :class="bedOccupancyClass"
                                role="progressbar"
                                :style="{ width: bedOccupancyPercent + '%' }"
                            >
                              {{ bedOccupancyPercent }}%
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4 text-end">
                            <span class="text-muted">
                              {{ dashboard.beds?.occupiedBeds || 0 }} / {{ dashboard.beds?.totalBeds || 0 }} beds
                            </span>
                        </div>
                      </div>
                      <div class="mt-2">
                          <span class="badge bg-success me-2">
                            <i class="mdi mdi-bed-empty me-1"></i>
                            {{ dashboard.beds?.availableBeds || 0 }} Available
                          </span>
                        <span class="badge bg-danger">
                            <i class="mdi mdi-bed me-1"></i>
                            {{ dashboard.beds?.occupiedBeds || 0 }} Occupied
                          </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Quick Actions -->
              <div class="col-lg-4">
                <div class="card">
                  <div class="card-body">
                    <h4 class="card-title mb-4">
                      <i class="mdi mdi-lightning-bolt me-2"></i>Quick Actions
                    </h4>

                    <div class="d-grid gap-2">
                      <button @click="goTo('patient-register')" class="btn btn-primary">
                        <i class="mdi mdi-account-plus me-2"></i>Register Patient
                      </button>
                      <button @click="goTo('new-visit')" class="btn btn-success">
                        <i class="mdi mdi-calendar-plus me-2"></i>Create Visit
                      </button>
                      <button @click="goTo('queue-management')" class="btn btn-warning">
                        <i class="mdi mdi-format-list-numbered me-2"></i>Queue Management
                      </button>
                      <button @click="goTo('pharmacy-dashboard')" class="btn btn-info">
                        <i class="mdi mdi-pill me-2"></i>Pharmacy Queue
                      </button>
                      <button @click="goTo('lab-dashboard')" class="btn btn-secondary">
                        <i class="mdi mdi-flask me-2"></i>Lab Requests
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Recent Activity -->
                <div class="card">
                  <div class="card-body">
                    <h4 class="card-title mb-4">
                      <i class="mdi mdi-history me-2"></i>Recent Activity
                    </h4>

                    <div v-if="recentVisits.length === 0" class="text-center text-muted py-3">
                      <i class="mdi mdi-information-outline font-size-24 d-block mb-2"></i>
                      No recent activity
                    </div>

                    <ul v-else class="list-unstyled activity-feed mb-0">
                      <li v-for="(visit, index) in recentVisits" :key="index" class="feed-item pb-3">
                        <div class="feed-item-list">
                          <p class="text-muted mb-1">
                            <small>{{ formatTime(visit.dateCreated) }}</small>
                          </p>
                          <p class="mb-0">
                            <strong>{{ visit.patientName }}</strong>
                            <span class="badge ms-2" :class="visitStatusBadge(visit.visitStatus)">
                                {{ visit.visitStatus }}
                              </span>
                          </p>
                          <small class="text-muted">{{ visit.visitType }} - Queue #{{ visit.queueNo }}</small>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Facility Selected Message -->
            <div v-if="!selectedFacility" class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body text-center py-5">
                    <div class="avatar-lg mx-auto mb-4">
                        <span class="avatar-title bg-light text-primary rounded-circle">
                          <i class="mdi mdi-hospital-building font-size-48"></i>
                        </span>
                    </div>
                    <h4>Welcome to Hospital Management</h4>
                    <p class="text-muted mb-4">Select a facility from the dropdown above to view dashboard statistics and start managing patients.</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">
                  County Hospital Management System
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import FacilitySelector from "@/components/hospital/FacilitySelector.vue";
import { execute, hospital } from "@/api";

export default {
  name: "HospitalDashboard",

  components: {
    NavigationBar,
    FacilitySelector
  },

  data() {
    return {
      loading: false,
      message: null,
      selectedFacility: null,
      currentDate: '',

      dashboard: {
        todayVisits: 0,
        activeVisits: 0,
        admittedPatients: 0,
        todayRevenue: 0,
        pendingLab: 0,
        pendingPrescriptions: 0,
        beds: {
          totalBeds: 0,
          availableBeds: 0,
          occupiedBeds: 0
        }
      },

      queueCounts: {
        triageQueue: 0,
        labPending: 0,
        prescriptionsPending: 0,
        lowStockAlerts: 0,
        totalNotifications: 0
      },

      recentVisits: []
    }
  },

  computed: {
    bedOccupancyPercent() {
      if (!this.dashboard.beds || !this.dashboard.beds.totalBeds) return 0;
      return Math.round((this.dashboard.beds.occupiedBeds / this.dashboard.beds.totalBeds) * 100);
    },

    bedOccupancyClass() {
      const percent = this.bedOccupancyPercent;
      if (percent >= 90) return 'bg-danger';
      if (percent >= 70) return 'bg-warning';
      return 'bg-success';
    }
  },

  mounted() {
    this.setCurrentDate();
    this.checkStoredFacility();
  },

  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num);
    },

    setCurrentDate() {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      this.currentDate = new Date().toLocaleDateString('en-GB', options);
    },

    checkStoredFacility() {
      const storedFacilityID = sessionStorage.getItem("hospitalFacilityID");
      const storedFacilityName = sessionStorage.getItem("hospitalFacilityName");

      if (storedFacilityID && storedFacilityName) {
        this.selectedFacility = {
          facilityID: storedFacilityID,
          facilityName: storedFacilityName
        };
        this.loadDashboardData();
      }
    },

    handleFacilityChange(facility) {
      this.selectedFacility = facility;
      if (facility) {
        this.loadDashboardData();
      } else {
        this.resetDashboard();
      }
    },

    loadDashboardData() {
      this.getHospitalDashboard();
      this.getNotificationCounts();
      this.getRecentVisits();
    },

    refreshDashboard() {
      if (this.selectedFacility) {
        this.loadDashboardData();
      }
    },

    resetDashboard() {
      this.dashboard = {
        todayVisits: 0,
        activeVisits: 0,
        admittedPatients: 0,
        todayRevenue: 0,
        beds: { totalBeds: 0, availableBeds: 0, occupiedBeds: 0 }
      };
      this.queueCounts = {
        triageQueue: 0,
        labPending: 0,
        prescriptionsPending: 0,
        lowStockAlerts: 0
      };
      this.recentVisits = [];
    },

    getHospitalDashboard() {
      this.loading = true;
      this.message = null;

      const data = new FormData();
      data.append("function", "getHospitalDashboard");
      data.append("facilityID", this.selectedFacility.facilityID);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.dashboard = {
                todayVisits: res.data.data.todayVisits || 0,
                activeVisits: res.data.data.activeVisits || 0,
                admittedPatients: res.data.data.admittedPatients || 0,
                todayRevenue: res.data.data.todayRevenue || 0,
                beds: res.data.data.beds || { totalBeds: 0, availableBeds: 0, occupiedBeds: 0 }
              };
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message;
          });
    },

    getNotificationCounts() {
      const data = new FormData();
      data.append("function", "getNotificationCounts");
      data.append("facilityID", this.selectedFacility.facilityID);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.queueCounts = {
                triageQueue: res.data.data.triageQueue || 0,
                labPending: res.data.data.labPending || 0,
                prescriptionsPending: res.data.data.prescriptionsPending || 0,
                lowStockAlerts: res.data.data.lowStockAlerts || 0,
                totalNotifications: res.data.data.totalNotifications || 0
              };
            }
          })
          .catch((e) => {
            console.error("Error fetching notification counts:", e.message);
          });
    },

    getRecentVisits() {
      const data = new FormData();
      data.append("function", "getActiveVisits");
      data.append("facilityID", this.selectedFacility.facilityID);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.recentVisits = (res.data.data.visits || []).slice(0, 5);
            }
          })
          .catch((e) => {
            console.error("Error fetching recent visits:", e.message);
          });
    },

    formatTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    visitStatusBadge(status) {
      const badges = {
        'WAITING_TRIAGE': 'bg-danger',
        'IN_TRIAGE': 'bg-danger',
        'WAITING_CONSULTATION': 'bg-warning',
        'IN_CONSULTATION': 'bg-primary',
        'WAITING_LAB': 'bg-info',
        'WAITING_PHARMACY': 'bg-success',
        'WAITING_PROCEDURE': 'bg-secondary',
        'ADMITTED': 'bg-dark',
        'COMPLETED': 'bg-success',
        'CANCELLED': 'bg-secondary'
      };
      return badges[status] || 'bg-secondary';
    }
  }
}
</script>

<style scoped>
.mini-stats-wid .card-body {
  padding: 20px;
}

.mini-stat-icon {
  width: 48px;
  height: 48px;
}

.avatar-sm {
  width: 3rem;
  height: 3rem;
}

.avatar-lg {
  width: 5rem;
  height: 5rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
}

.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.1); }
.bg-success-subtle { background-color: rgba(52, 195, 143, 0.1); }
.bg-warning-subtle { background-color: rgba(241, 180, 76, 0.1); }
.bg-danger-subtle { background-color: rgba(244, 106, 106, 0.1); }
.bg-info-subtle { background-color: rgba(80, 165, 241, 0.1); }

.activity-feed {
  padding-left: 20px;
  border-left: 3px solid #e9ecef;
}

.feed-item {
  position: relative;
}

.feed-item::before {
  content: '';
  position: absolute;
  left: -26px;
  top: 5px;
  width: 10px;
  height: 10px;
  background: #556ee6;
  border-radius: 50%;
}

.btn-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
  border: none;
}

.btn-soft-primary:hover {
  background-color: #556ee6;
  color: #fff;
}
</style>